/**
 * Replaces KeyGate. Fetches `GET /v1/me` once; while pending it renders nothing, so no
 * screen flashes before the answer arrives.
 *
 * It also registers the global 401 handler, which is the fix for the known bug where an
 * expired session renders a 401 inside a shell the user is no longer authorized to see.
 * A 403 does nothing here: the screen shows its own `Failed`.
 */
import { useEffect, type ReactElement, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { setUnauthenticatedHandler } from "../lib/api";
import { useSession } from "../hooks/useSession";

export function SessionGate({ children }: { children: ReactNode }): ReactElement {
  const location = useLocation();
  const { me, isPending } = useSession();

  useEffect(() => {
    const next = encodeURIComponent(location.pathname + location.search);
    setUnauthenticatedHandler(() => {
      // A hard navigation, not a router push: every cached query belongs to a session
      // that no longer exists, and reloading is the cheapest way to be sure none of it
      // survives.
      window.location.assign(`/login?next=${next}`);
    });
    return () => setUnauthenticatedHandler(null);
  }, [location.pathname, location.search]);

  if (isPending) return <></>;
  if (!me) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}
